import Component from '@glimmer/component';
import { t } from 'ember-intl';
import CopyButton from 'scaleflow/components/atoms/copy-button';

/**
 * The single folder the whole guide is built around. Everything downstream —
 * the clone step, the loop, the "upload everything in here" step — refers to
 * this one name, so it lives in one place.
 */
export const ARCHIVE_FOLDER = 'xray';

/**
 * Sets the folder name apart from the prose wherever the guide names it.
 *
 * Held here rather than in the locale files: Tailwind's content scan reads
 * `./app/**` only, so classes written in `translations/*.yaml` are purged.
 * (`financials.explanation` gets away with an `underline` in its markup purely
 * because that class is also used somewhere under `app/`.)
 */
const FOLDER_CHIP =
  'rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.95em] text-gray-800 ring-1 ring-inset ring-gray-200';

/**
 * What a shell inside the folder looks like, so both blocks read as a session
 * someone is part-way through rather than as free-floating snippets.
 *
 * Rendered from `data-prompt` via a `before:` pseudo-element rather than as
 * text: browsers do not include generated content in a selection, so neither
 * the copy button nor a hand-drag can put `~/xray $` into someone's shell.
 */
const PROMPT = `~/${ARCHIVE_FOLDER} $ `;

/** bash's PS2, shown on the loop's continuation lines as a real terminal would. */
const CONTINUATION = '> ';

const TERMINAL_BLOCK = 'rounded-md bg-gray-900 px-4 py-3 overflow-x-auto';
const TERMINAL_LINE =
  'text-xs leading-relaxed text-gray-100 font-mono whitespace-pre before:content-[attr(data-prompt)] before:text-gray-500';

/**
 * Illustrative only — nobody's repositories live at these URLs. Two lines
 * rather than one because the point being made is "run this once per
 * repository", which a single line does not show.
 */
export const CLONE_EXAMPLE = [
  'git clone https://github.com/your-org/payments.git',
  'git clone https://github.com/your-org/web.git',
];

/**
 * One archive per repository, for every repository in the folder.
 *
 * **Never add `--exclude` flags here**, least of all for `.git`. Commit history
 * is what the analysis reads for contributor activity, commit frequency, review
 * patterns and team velocity, so an archive without it produces a visibly
 * thinner report than the same repository would if it had been cloned through
 * the GitHub connector — and it does so silently: the upload succeeds and the
 * scan succeeds. The test asserts this command carries no exclusions at all.
 *
 * The trailing-slash glob matches directories only, so the `.tar.gz` files this
 * writes alongside the clones are never themselves archived, and re-running is
 * safe. Stripping that slash back off names the archive `payments.tar.gz`
 * rather than `payments/.tar.gz`; `tar` is happy to keep it on the input.
 *
 * Both operands are quoted because a cloned folder name is not guaranteed to
 * be a single shell word, and the filename becomes the repository name in the
 * X-Ray — an unquoted `my repo` would archive the wrong paths and carry the
 * mistake all the way into the report.
 */
export function buildArchiveCommand(): string {
  return [
    'for repo in */; do',
    '  tar -czf "${repo%/}.tar.gz" "$repo"',
    'done',
  ].join('\n');
}

export interface ManualUploadsArchiveCommandSignature {
  Element: HTMLDivElement;
  Args: { Named: Record<string, never> };
}

type TerminalLine = { prompt: string; text: string };

export default class ManualUploadsArchiveCommand extends Component<ManualUploadsArchiveCommandSignature> {
  /** What the copy button puts on the clipboard — prompts are display only. */
  get command(): string {
    return buildArchiveCommand();
  }

  get cloneLines(): TerminalLine[] {
    return CLONE_EXAMPLE.map((text) => ({ prompt: PROMPT, text }));
  }

  get commandLines(): TerminalLine[] {
    const [first = '', ...rest] = buildArchiveCommand().split('\n');
    return [
      { prompt: PROMPT, text: first },
      ...rest.map((text) => ({ prompt: CONTINUATION, text })),
    ];
  }

  <template>
    <div
      class="rounded-lg border bg-white"
      data-test-archive-command
      ...attributes
    >
      <div class="px-6 py-5">
        <h2 class="font-semibold text-gray-900">
          {{t "manual-uploads.guide-title"}}
        </h2>
        <p class="text-sm text-gray-500 mt-1">
          {{t "manual-uploads.guide-subtitle"}}
        </p>

        {{! Step 1 — the folder }}
        <div class="mt-5" data-test-step-folder>
          <div class="flex items-baseline gap-2">
            <span
              class="flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-medium shrink-0"
            >1</span>
            <div>
              <span class="text-sm font-medium text-gray-900">
                {{t "manual-uploads.guide-step-folder"}}
                <code
                  class={{FOLDER_CHIP}}
                  data-test-folder-name
                >{{ARCHIVE_FOLDER}}</code>
              </span>
              <p class="text-xs text-gray-500 mt-0.5">
                {{t "manual-uploads.guide-step-folder-hint"}}
              </p>
            </div>
          </div>
        </div>

        {{! Step 2 — the clones }}
        <div class="mt-5" data-test-step-clone>
          <div class="flex items-baseline gap-2">
            <span
              class="flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-medium shrink-0"
            >2</span>
            <div>
              <span class="text-sm font-medium text-gray-900">
                {{t "manual-uploads.guide-step-clone"}}
                <code
                  class={{FOLDER_CHIP}}
                  data-test-folder-name
                >{{ARCHIVE_FOLDER}}</code>
              </span>
              <p class="text-xs text-gray-500 mt-0.5">
                {{t "manual-uploads.guide-step-clone-hint"}}
              </p>
            </div>
          </div>

          <div class="{{TERMINAL_BLOCK}} ml-7 mt-2" data-test-clone-example>
            {{#each this.cloneLines as |line|}}
              <div
                class={{TERMINAL_LINE}}
                data-prompt={{line.prompt}}
              >{{line.text}}</div>
            {{/each}}
          </div>
        </div>

        {{! The one thing users are most likely to get wrong, next to the step
            where they would get it wrong. }}
        <div
          class="mt-3 ml-7 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
          data-test-keep-git
        >
          <p class="text-xs text-amber-900">
            <span class="font-semibold">{{t
                "manual-uploads.keep-git-title"
              }}</span>
            {{t "manual-uploads.keep-git-body"}}
          </p>
        </div>

        {{! Step 3 — the command }}
        <div class="mt-5" data-test-step-archive>
          <div class="flex items-baseline gap-2 mb-2">
            <span
              class="flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-medium shrink-0"
            >3</span>
            <span class="text-sm font-medium text-gray-900">
              {{t "manual-uploads.guide-step-run"}}
              <code
                class={{FOLDER_CHIP}}
                data-test-folder-name
              >{{ARCHIVE_FOLDER}}</code>
            </span>
          </div>

          <div class="ml-7">
            <div class={{TERMINAL_BLOCK}} data-test-command>
              {{#each this.commandLines as |line|}}
                <div
                  class={{TERMINAL_LINE}}
                  data-prompt={{line.prompt}}
                >{{line.text}}</div>
              {{/each}}
            </div>
            <div class="flex items-center justify-between gap-3 mt-2 flex-wrap">
              <p class="text-xs text-gray-500">
                {{t "manual-uploads.guide-command-hint"}}
                <code
                  class={{FOLDER_CHIP}}
                  data-test-folder-name
                >{{ARCHIVE_FOLDER}}</code>.
              </p>
              <CopyButton
                @value={{this.command}}
                @label={{t "manual-uploads.copy-command"}}
              />
            </div>
            <p class="text-xs text-gray-500 mt-2">
              {{t "manual-uploads.guide-windows"}}
            </p>
          </div>
        </div>

        {{! Step 4 — the upload }}
        <div class="mt-5" data-test-step-upload>
          <div class="flex items-baseline gap-2">
            <span
              class="flex items-center justify-center w-5 h-5 rounded-full bg-primary-600 text-white text-xs font-medium shrink-0"
            >4</span>
            <div>
              <span class="text-sm font-medium text-gray-900">
                {{t "manual-uploads.guide-step-upload"}}
              </span>
              <p class="text-xs text-gray-500 mt-0.5">
                {{t "manual-uploads.guide-step-upload-hint"}}
                <code
                  class={{FOLDER_CHIP}}
                  data-test-folder-name
                >{{ARCHIVE_FOLDER}}</code>.
                {{t "manual-uploads.guide-step-upload-note"}}
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </template>
}
